import { Link } from "react-router-dom";
import {
  ArrowRight, Building2, Calendar, FileText, MapPin, User, Hash,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSeo } from "@/lib/seo";

const facts = [
  { icon: Building2, label: "Legal name", value: "HILDRETH TECHNOLOGIES LLC" },
  { icon: FileText, label: "Entity type", value: "Florida limited liability company" },
  { icon: Calendar, label: "Established", value: "July 8, 2026" },
  { icon: Hash, label: "Florida document number", value: "L26000363430" },
  { icon: MapPin, label: "Location", value: "Okeechobee, Florida" },
  { icon: User, label: "Founder & Managing Member", value: "Lisa Hildreth" },
];

export default function About() {
  useSeo({
    title: "About — Founder-Led Florida Technology Company",
    description:
      "HILDRETH TECHNOLOGIES LLC is a founder-led Florida technology company established in 2026, creating practical digital products across healthcare, policy, travel, privacy, media, and creator technology.",
    path: "/about",
  });

  return (
    <div>
      <section className="relative overflow-hidden border-b border-slate-800/70">
        <div className="absolute inset-0 ht-hero-glow" />
        <div className="relative mx-auto max-w-7xl px-5 py-20 sm:px-8">
          <p className="text-sm font-semibold uppercase tracking-wider text-sky-400">
            About
          </p>
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl">
            HILDRETH TECHNOLOGIES LLC
          </h1>
          <p className="mt-5 max-w-3xl text-lg leading-relaxed text-slate-300">
            HILDRETH TECHNOLOGIES LLC is a founder-led Florida technology company
            established in 2026. The company creates practical digital products
            across healthcare education, medical policy research, travel
            intelligence, privacy, secure networking, digital publishing, and
            creator technology.
          </p>
        </div>
      </section>

      {/* Founder */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <p className="text-sm font-semibold uppercase tracking-wider text-teal-300">
              The Founder
            </p>
            <h2 className="mt-3 font-display text-3xl font-semibold text-white">
              Lisa Hildreth
            </h2>
            <p className="mt-2 text-base font-medium text-slate-400">
              Founder and Managing Member
            </p>
            <p className="mt-6 text-base leading-relaxed text-slate-300">
              Lisa Hildreth is the founder and Managing Member of HILDRETH
              TECHNOLOGIES LLC. She holds a master's degree in Technology
              Management and a bachelor's degree.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-300">
              Lisa began developing websites, applications, and digital products
              in 2024. Her experience in technology management, healthcare policy,
              research, business operations, digital product development, and
              software project management informs the company's focus on
              practical, understandable technology designed around real-world
              needs.
            </p>
            <div className="mt-8 rounded-xl border border-slate-800 bg-[#0B1527] p-6">
              <p className="font-display text-lg font-medium text-white">
                “Practical technology built to solve real-world problems.”
              </p>
              <p className="mt-2 text-sm text-slate-400">
                This principle guides every product the company designs and
                develops.
              </p>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="flex h-56 items-center justify-center rounded-2xl border border-slate-800 bg-gradient-to-br from-sky-500/15 to-teal-500/10">
              <span
                className="flex h-24 w-24 items-center justify-center rounded-2xl font-display text-3xl font-extrabold text-white shadow-xl"
                style={{ background: "linear-gradient(135deg,#0EA5E9,#0D9488)" }}
              >
                HT
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Company facts */}
      <section className="border-y border-slate-800/70 bg-[#0B1527]/40">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
          <h2 className="font-display text-3xl font-semibold text-white">
            Company facts
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {facts.map((f) => (
              <div
                key={f.label}
                data-testid={`fact-${f.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="rounded-xl border border-slate-800 bg-[#0B1527] p-6"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-500/15 text-sky-300">
                  <f.icon size={20} />
                </span>
                <p className="mt-4 text-xs font-medium uppercase tracking-wider text-slate-500">
                  {f.label}
                </p>
                <p className="mt-1 text-base font-semibold text-white">
                  {f.value}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 text-center sm:px-8">
        <h2 className="mx-auto max-w-2xl font-display text-3xl font-semibold text-white">
          Build, partner, or learn more
        </h2>
        <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
          <Button
            asChild
            size="lg"
            data-testid="about-work-cta"
            className="rounded-full bg-sky-500 px-7 font-semibold text-[#040B16] hover:bg-sky-400"
          >
            <Link to="/our-work">
              View Our Work <ArrowRight size={18} className="ml-2" />
            </Link>
          </Button>
          <a
            href="/contact"
            data-testid="about-contact-cta"
            className="inline-flex h-10 items-center justify-center rounded-full border border-slate-600 bg-transparent px-7 font-semibold text-white transition-colors hover:bg-slate-800"
          >
            Contact Us
          </a>
        </div>
      </section>
    </div>
  );
}
